import React from 'react'
import { BADGE_CLASS_NAME } from './classnames'
import { badgeShapeMap, DEFAULT_BADGE_SHAPE, getBadgeColorConfig, type BadgeColorConfig } from './constants'
import { type BadgeColor, type BadgeProps } from './types'
import type { ThemeColor } from '../theme/palette'
import { useTheme } from '../../utils/useTheme'
import type { Theme } from '../theme/themeTypes'

const getColorConfig = (color: BadgeColor | ThemeColor, theme: Theme): BadgeColorConfig => {
  const colorConfig = getBadgeColorConfig(color, theme)
  if (colorConfig == null) {
    return {
      backgroundColor: color as ThemeColor,
      borderColor: {
        lighter: color as ThemeColor,
        darker: color as ThemeColor
      }
    }
  }

  return colorConfig
}

const Badge = ({
  color,
  opacity,
  variant = 'darker',
  shape = DEFAULT_BADGE_SHAPE,
  ...svgProps
}: BadgeProps): React.ReactElement => {
  const theme = useTheme()
  const colorConfig = getColorConfig(color, theme)
  const backgroundColor = colorConfig.backgroundColor
  const borderColor = colorConfig.borderColor?.[variant] ?? backgroundColor
  const badgeShape = badgeShapeMap[shape]

  if (badgeShape.hasBorder) {
    const BadgeShape = badgeShape.component

    return (
      <BadgeShape
        {...svgProps}
        className={BADGE_CLASS_NAME}
        style={{ flexShrink: 0, ...(opacity != null ? { opacity } : {}) }}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
      />
    )
  }

  const BadgeShape = badgeShape.component

  return (
    <BadgeShape
      {...svgProps}
      className={BADGE_CLASS_NAME}
      style={{ flexShrink: 0, ...(opacity != null ? { opacity } : {}) }}
      backgroundColor={backgroundColor}
    />
  )
}

export default Badge

export { Badge }
export type {
  BadgeBorderVariant,
  BadgeColor,
  BadgeProps,
  BadgeShape,
  BorderedBadgeShape,
  LegacyBadgeColor
} from './types'
export {
  colorMap,
  createColorMap,
  legacyBadgeColorMigrationMap,
  legacyBadgeColors,
  tokenBadgeColors
} from './constants'
